/** @import {MyProfile} from './MyProfileTypes' */
/** @import {CustomVerification} from './OTPModalTypes' */
/** @import {MobileNumberProps} from './MobileNumberTypes' */

import React, { useState, useEffect } from 'react';
import { gql } from 'apollo-boost';
import { useMutation } from '@apollo/react-hooks';
import TelInput from '../Form/TelInput/TelInput';
import Section from './Section';
import OTPModal from './OTPModal';

function MobileNumberOTPModal(/** @type {MobileNumberProps} */ { mobileNumber, onClose, onComplete, verificationToken, customVerification }) {
  return (
    <OTPModal
      title="Update mobile"
      mobileNumber={mobileNumber}
      successMessage="Your mobile number has been updated."
      onClose={onClose}
      onComplete={onComplete}
      verificationToken={verificationToken}
      customVerification={customVerification}
      isResendActivationOtp={true}
      analyticsRegion={"my profile"}
      analyticsAction={"Mobile number change"} 
    />
  );
}

const VALIDATE_MOBILE_NUMBER = gql`
  mutation validateMobileNumber($request : ValidateMobileNumberRequestType!){
    validateMobileNumber(request:$request) {
      success
    }
  }
`;
  
const UPDATE_SMS_FACTOR = gql`
  mutation updateSmsFactor($request : UpdateSmsFactorRequestType!){
    updateSmsFactor(request:$request) {
      success
      verificationToken
    }
  }  
`;

const UPDATE_MOBILE_NUMBER = gql`
  mutation updateMobileNumber($request : UpdateMobileNumberRequestType!){
    updateMobileNumber(request:$request) {
      success
    }
  }  
`;

function MobileNumber(/** @type {{ profile: MyProfile; onSave: (props: { number: string; }) => void; }} */ { profile, onSave }) {
  const [editing, setEditing] = useState(false);
  const [number, setNumber] = useState(profile.mobileNumber);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [validateMobileNumber] = useMutation(VALIDATE_MOBILE_NUMBER);
  const [updateSmsFactor] = useMutation(UPDATE_SMS_FACTOR);
  const [updateMobileNumber] = useMutation(UPDATE_MOBILE_NUMBER);
  const [verificationToken, setVerificationToken] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [mobileNumberInUseError, setMobileNumberInUseError] = useState(false);
  const [invalidOtpError, setInvalidOtpError] = useState(false);
  const [unexpectedError, setUnexpectedError] = useState(false);

  const cancelEditing = () => {
    setNumber(profile.mobileNumber);
    setShowValidation(false);
  };

  const startEditing = (/** @type {boolean} */ editing) => {
    if (editing) {
        setNumber('');
        setIsValid(false);
    }

    setEditing(editing);
  }

  const mobileValidation = async (/** @type {string} */ number) => {
      try {
          await validateMobileNumber({
              variables: {
                  request: {
                      mobileNumber: number
                  }
              }
          });
      }
      catch (e) {
          if (e instanceof Error && e.message.includes("MobileNumberInUseError")) {
              setMobileNumberInUseError(true);
          } else {
              setUnexpectedError(true);
          }
          setShowValidation(true);
          return false;
      }

      return true;
  };

  const setMobileNumberChange = (/** @type {string} */ number) => {
    setNumber(number);
    setShowValidation(number.length > 3 && !number.startsWith('+44'));
    setIsValid(number.length === 13 && number.startsWith('+44'));
  }
  
  const approveEditing = async () => {
    // First do server validation before proceeding
    const validationResult = await mobileValidation(number);
    
    if (!validationResult) {
      // Show validation error and don't proceed
      return;
    }
    
    setOtpModalOpen(true);
    
    var result = await updateSmsFactor({
      variables: {
        request: {
          mobileNumber: number
        }
      }
    });

    if (result.data.updateSmsFactor.verificationToken) {
      setVerificationToken(result.data.updateSmsFactor.verificationToken);
    }
  };

  const onModalCancel = () => {
    setOtpModalOpen(false);
    cancelEditing();
  };

  const onModalComplete = () => {
    setOtpModalOpen(false);
    onSave && onSave({ number });
  };

    /** @type {CustomVerification} */
    const customVerification = async (/** @type {unknown} */ otp) => {
    let result;
    
    try {
      result = await updateMobileNumber({
        variables: {
          request: {
            mobileNumber: number,
            activateOtp: {
              passCode: otp,
              verificationToken: verificationToken
            }
          }
        }
      });
    }
    catch (e) {
      if (e instanceof Error && e.message.includes("InvalidOtpError")) {
        setInvalidOtpError(true);
      }
      else {
        setUnexpectedError(true);
      }
    }

    return {
      success: result?.data.updateMobileNumber.success,
      data: {approved: true}
    }
  }

  useEffect(() => {
    setNumber(profile.mobileNumber);
  }, [profile.mobileNumber]);
  
  return (
    <Section
        title="Mobile number"
        modifiers={['space-saver', 'margin-bottom']}
        editing={editing}
        setEditing={startEditing}
        onCancel={cancelEditing}
        onSave={() => { void approveEditing(); }} // Void to indicate we're intentionally ignoring the promise
        valid={isValid}>
      <TelInput
          value={number}
          onChange={setMobileNumberChange}
          disabled={!editing}
          prependValue='+44'
          pattern='^\+\d*'
          maxLength={13}
          hasError={showValidation}
      />
      {otpModalOpen && <MobileNumberOTPModal
          mobileNumber={number}
          onClose={onModalCancel}
          onComplete={onModalComplete}
          verificationToken={verificationToken}
          customVerification={customVerification} />}
      <div className={showValidation && !number.startsWith('+44') ? "login__error" : "login__error login__error--hide"}>
        <span className="login__error--wrap">Please enter a valid UK mobile number starting with +44.</span>
      </div>
      <div className={showValidation && unexpectedError ? "login__error" : "login__error login__error--hide"}>
        <span className="login__error--wrap">An unexpected error occurred, please try again later.</span>
      </div>
      <div className={showValidation && invalidOtpError ? "login__error" : "login__error login__error--hide"}>
        <span className="login__error--wrap">Your code has either expired or been entered incorrectly. Please try again or click below to resend a new code.</span>
      </div>
      <div className={showValidation && mobileNumberInUseError ? "login__error" : "login__error login__error--hide"}>
        <span className="login__error--wrap">Looks like this mobile number is already in use. Please use another mobile number.</span>
      </div>
    </Section>
  );
};

export default MobileNumber;
